import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Tag, X, Mail, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import { Field, Panel, DelBtn } from "@/pages/admin/adminShared";

const imgSrc = (v) => (v.startsWith("http") ? v : `${process.env.REACT_APP_BACKEND_URL}${v}`);

export function WinnersAdmin({ items, onCreate, onUpdate, onDelete }) {
  const empty = { name: "", event: "", prize: "", photo: "", date: "" };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const startEdit = (w) => { setEditingId(w.id); setF({ ...empty, ...w }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setF(empty); };
  const submit = async (e) => { e.preventDefault(); const ok = editingId ? await onUpdate(editingId, f) : await onCreate(f); if (ok) cancel(); };
  return (
    <Panel title={editingId ? "Edit Winner" : "Add Winner"} form={
      <form onSubmit={submit} className="space-y-3" data-testid="winner-form">
        <Field label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="Event" value={f.event} onChange={(e) => setF({ ...f, event: e.target.value })} />
        <Field label="Prize" value={f.prize} onChange={(e) => setF({ ...f, prize: e.target.value })} placeholder="$3,200" />
        <ImageUpload label="Winner Photo" value={f.photo} onChange={(url) => setF({ ...f, photo: url })} testId="winner-image-upload" />
        <Field label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <div className="flex gap-2">
          <Button type="submit" className="flex-1 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="winner-submit">{editingId ? "Save Changes" : "Add Winner"}</Button>
          {editingId && <Button type="button" onClick={cancel} variant="outline" className="rounded-none border-slate-600 text-slate-300 text-xs" data-testid="winner-cancel">Cancel</Button>}
        </div>
      </form>
    }>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((w) => (
          <div key={w.id} className="bg-navy-paper border border-slate-800 overflow-hidden" data-testid={`admin-winner-${w.id}`}>
            {w.photo && <img src={imgSrc(w.photo)} alt="" className="w-full h-32 object-cover" />}
            <div className="p-3 flex items-center justify-between"><div><div className="text-white text-sm font-medium">{w.name}</div><div className="text-gold text-xs">{w.prize}</div></div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(w)} className="text-slate-500 hover:text-gold transition-colors" data-testid={`admin-edit-winner-${w.id}`}><Pencil className="w-4 h-4" /></button>
                <DelBtn onClick={() => onDelete(w.id)} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function NewsAdmin({ items, onCreate, onUpdate, onDelete }) {
  const empty = { title: "", body: "", image: "", date: "", hidden: false };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const startEdit = (n) => { setEditingId(n.id); setF({ ...empty, ...n }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setF(empty); };
  const submit = async (e) => { e.preventDefault(); const ok = editingId ? await onUpdate(editingId, f) : await onCreate(f); if (ok) cancel(); };
  return (
    <Panel title={editingId ? "Edit News" : "Post News"} form={
      <form onSubmit={submit} className="space-y-3" data-testid="news-form">
        <Field label="Title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <div><Label className="text-slate-300 text-xs">Body</Label><Textarea required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" rows={4} /></div>
        <ImageUpload label="Image" value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="news-image-upload" />
        <Field label="Date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <label className="flex items-center gap-2 text-slate-300 text-sm" data-testid="news-hidden-toggle"><input type="checkbox" checked={!!f.hidden} onChange={(e) => setF({ ...f, hidden: e.target.checked })} /> Hide from public website</label>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="news-submit">{editingId ? "Save Changes" : "Publish"}</Button>
          {editingId && <Button type="button" onClick={cancel} variant="outline" className="rounded-none border-slate-600 text-slate-300 text-xs" data-testid="news-cancel">Cancel</Button>}
        </div>
      </form>
    }>
      <div className="space-y-2">
        {items.map((n) => (
          <div key={n.id} className="bg-navy-paper border border-slate-800 p-4 flex items-center justify-between" data-testid={`admin-news-${n.id}`}>
            <div><div className="text-white font-medium text-sm flex items-center gap-2">{n.title}{n.hidden && <span className="text-slate-500 text-[9px] uppercase tracking-widest border border-slate-700 px-1.5 py-0.5" data-testid={`news-hidden-badge-${n.id}`}>Hidden</span>}</div><div className="text-slate-500 text-xs">{n.date}</div></div>
            <div className="flex items-center gap-2">
              <button onClick={() => startEdit(n)} className="text-slate-500 hover:text-gold transition-colors" data-testid={`admin-edit-news-${n.id}`}><Pencil className="w-4 h-4" /></button>
              <DelBtn onClick={() => onDelete(n.id)} />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function VenuesAdmin({ items, onCreate, onUpdate, onDelete }) {
  const empty = { name: "", city: "", address: "", schedule: "", description: "", image: "", website: "" };
  const [f, setF] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const startEdit = (v) => { setEditingId(v.id); setF({ ...empty, ...v }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditingId(null); setF(empty); };
  const submit = async (e) => { e.preventDefault(); const ok = editingId ? await onUpdate(editingId, f) : await onCreate(f); if (ok) cancel(); };
  return (
    <Panel title={editingId ? "Edit Venue" : "Add Venue"} form={
      <form onSubmit={submit} className="space-y-3" data-testid="venue-form">
        <Field label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="City / Town" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
        <Field label="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        <Field label="Schedule" value={f.schedule} onChange={(e) => setF({ ...f, schedule: e.target.value })} placeholder="Mondays 6:30 PM" />
        <Field label="Website Link" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="https://… (leave blank to hide on venue page)" />
        <ImageUpload label="Venue Image" value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="venue-image-upload" />
        <div><Label className="text-slate-300 text-xs">Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" rows={3} /></div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="venue-submit">{editingId ? "Save Changes" : "Add Venue"}</Button>
          {editingId && <Button type="button" onClick={cancel} variant="outline" className="rounded-none border-slate-600 text-slate-300 text-xs" data-testid="venue-cancel">Cancel</Button>}
        </div>
      </form>
    }>
      <div className="space-y-2">
        {items.map((v) => (
          <div key={v.id} className="bg-navy-paper border border-slate-800 p-4 flex items-center justify-between" data-testid={`admin-venue-${v.id}`}>
            <div className="flex items-center gap-3">{v.image && <img src={imgSrc(v.image)} alt="" className="w-12 h-12 object-cover" />}<div><div className="text-white font-medium text-sm">{v.name}</div><div className="text-slate-500 text-xs">{v.city} · {v.schedule}{v.website ? " · 🔗" : ""}</div></div></div>
            <div className="flex items-center gap-2">
              <button onClick={() => startEdit(v)} className="text-slate-500 hover:text-gold transition-colors" data-testid={`admin-edit-venue-${v.id}`}><Pencil className="w-4 h-4" /></button>
              <DelBtn onClick={() => onDelete(v.id)} />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function TagDialog({ photo, players, onClose, onSave }) {
  const [tags, setTags] = useState(photo?.tags || []);
  const [q, setQ] = useState("");
  useEffect(() => { setTags(photo?.tags || []); setQ(""); }, [photo]);
  const add = () => { const v = q.trim(); if (v && !tags.includes(v)) setTags([...tags, v]); setQ(""); };
  return (
    <Dialog open={!!photo} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-navy-paper border-slate-800 rounded-none" data-testid="tag-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display">Tag Players</DialogTitle>
          <DialogDescription className="text-slate-400">Tagged photos appear on each player's profile page.</DialogDescription>
        </DialogHeader>
        {photo && <img src={imgSrc(photo.image)} alt="" className="w-full h-40 object-cover border border-slate-800" />}
        <div className="flex gap-2">
          <input list="tag-player-options" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Type a player name" className="flex-1 bg-navy border border-slate-700 text-white text-sm px-3 py-2" data-testid="tag-input" />
          <datalist id="tag-player-options">{players.filter((p) => !tags.includes(p)).map((p) => <option key={p} value={p} />)}</datalist>
          <Button type="button" onClick={add} className="bg-gold text-navy hover:bg-gold-light rounded-none text-xs font-bold" data-testid="tag-add-btn">Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 min-h-[2rem]" data-testid="tag-list">
          {tags.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 border border-gold/40 text-gold text-xs px-2 py-1">{t}
              <button onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-white" data-testid={`tag-remove-${t}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
          {tags.length === 0 && <span className="text-slate-500 text-xs">No players tagged yet.</span>}
        </div>
        <DialogFooter>
          <Button onClick={async () => { if (await onSave({ ...photo, tags })) onClose(); }} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="tag-save-btn">Save Tags</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GalleryAlbumGroup({ name, photos, albums, onUpdate, onDelete, onTag }) {
  return (
    <div data-testid={`admin-album-${name || "none"}`}>
      <div className="text-gold text-xs uppercase tracking-widest mb-2">{name || "No album"} <span className="text-slate-500">({photos.length})</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((g) => (
          <div key={g.id} className="bg-navy-paper border border-slate-800" data-testid={`admin-gallery-${g.id}`}>
            <div className="relative">
              <img src={imgSrc(g.image)} alt="" className="w-full h-28 object-cover" />
              <button onClick={() => onDelete(g.id)} className="absolute top-1 right-1 bg-navy/80 p-1 text-slate-300 hover:text-destructive" data-testid={`admin-gallery-delete-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              <button onClick={() => onTag(g)} className="absolute bottom-1 left-1 bg-navy/85 px-2 py-1 text-gold text-[11px] flex items-center gap-1 hover:bg-gold hover:text-navy" data-testid={`admin-gallery-tag-${g.id}`}><Tag className="w-3 h-3" /> {g.tags?.length ? `${g.tags.length} tagged` : "Tag"}</button>
            </div>
            <select value={g.album || ""} onChange={(e) => { const v = e.target.value === "__new" ? (window.prompt("New album name") || "").trim() : e.target.value; if (e.target.value !== "__new" || v) onUpdate(g.id, { ...g, album: v }); }} className="w-full bg-navy border-t border-slate-800 text-slate-300 text-xs px-2 py-1.5" data-testid={`admin-gallery-album-${g.id}`}>
              <option value="">No album</option>
              {albums.map((a) => <option key={a} value={a}>{a}</option>)}
              <option value="__new">+ New album…</option>
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export function GalleryAdmin({ items, events = [], seasons = [], players = [], onCreate, onUpdate, onDelete }) {
  const empty = { image: "", caption: "", album: "" };
  const [f, setF] = useState(empty);
  const [tagging, setTagging] = useState(null);
  const submit = async (e) => { e.preventDefault(); const ok = await onCreate(f); if (ok) setF({ ...empty, album: f.album }); };
  const used = [...new Set(items.map((g) => g.album).filter(Boolean))];
  const suggestions = [...new Set([...used, ...seasons.map((s) => s.name || s), ...events.map((ev) => ev.title)].filter(Boolean))];
  const groups = [...used.sort(), ""].map((a) => [a, items.filter((g) => (g.album || "") === a)]).filter(([, p]) => p.length);
  return (
    <Panel title="Add Photo" form={
      <form onSubmit={submit} className="space-y-3" data-testid="gallery-form">
        <ImageUpload label="Photo" value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="gallery-image-upload" />
        <Field label="Caption" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} />
        <Field label="Album (event or season)" list="gallery-album-options" value={f.album} onChange={(e) => setF({ ...f, album: e.target.value })} placeholder="Pick or type a new album" data-testid="gallery-album-input" />
        <datalist id="gallery-album-options">{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
        <Button type="submit" disabled={!f.image} className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs disabled:opacity-50" data-testid="gallery-submit">Add Photo</Button>
      </form>
    }>
      <div className="space-y-6">
        {groups.map(([a, photos]) => <GalleryAlbumGroup key={a || "none"} name={a} photos={photos} albums={suggestions} onUpdate={onUpdate} onDelete={onDelete} onTag={setTagging} />)}
      </div>
      <TagDialog photo={tagging} players={players} onClose={() => setTagging(null)} onSave={(p) => onUpdate(p.id, p)} />
    </Panel>
  );
}

function ChampionStoryRow({ c }) {
  const [f, setF] = useState({ quote: c.quote || "", story: c.story || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/champion-stories", { season: c.season, ...f }); toast.success(`Saved story for ${c.name}`); }
    catch (e) { toast.error("Could not save story"); } finally { setSaving(false); }
  };
  return (
    <div className="bg-navy-paper border border-slate-800 p-4 space-y-3" data-testid={`champion-story-${c.season}`}>
      <div><div className="text-gold text-[10px] uppercase tracking-[0.3em]">{c.season}</div><div className="text-white font-medium">{c.name}</div></div>
      <Field label="Quote" value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} placeholder="A line from the champion" data-testid={`champion-quote-${c.season}`} />
      <div><Label className="text-slate-300 text-xs">Story</Label><Textarea rows={3} value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" placeholder="A short story about their season" data-testid={`champion-story-text-${c.season}`} /></div>
      <Button onClick={save} disabled={saving} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid={`champion-story-save-${c.season}`}>{saving ? "Saving…" : "Save"}</Button>
    </div>
  );
}

export function ChampionStoriesAdmin() {
  const [champs, setChamps] = useState(null);
  useEffect(() => { api.get("/champions").then((r) => setChamps(r.data)).catch(() => setChamps([])); }, []);
  return (
    <div className="mt-10" data-testid="champion-stories-admin">
      <h3 className="font-display text-xl font-bold text-white">Hall of Legends Stories</h3>
      <p className="text-slate-500 text-xs mt-1 mb-4">Add a quote and short story for each season champion. They appear on the Hall of Legends page.</p>
      {champs && champs.length === 0 && <p className="text-slate-500 text-sm">No season champions yet.</p>}
      <div className="grid md:grid-cols-2 gap-4">{(champs || []).map((c) => <ChampionStoryRow key={c.season} c={c} />)}</div>
    </div>
  );
}

export function RegistrationsView({ items }) {
  return (
    <div className="mt-6 border border-white/10 overflow-x-auto" data-testid="admin-registrations">
      <table className="w-full text-left text-sm min-w-[640px]">
        <thead className="bg-secondary text-gold text-xs uppercase"><tr><th className="px-4 py-3">Player</th><th className="px-4 py-3">Event</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th></tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-t border-white/5">
              <td className="px-4 py-2 text-white">{r.player_name}</td>
              <td className="px-4 py-2 text-slate-400">{r.event_title}</td>
              <td className="px-4 py-2" data-testid={`admin-reg-status-${r.id}`}><span className={["paid", "registered"].includes(r.payment_status) ? "text-accent" : "text-slate-400"}>{["paid", "registered"].includes(r.payment_status) ? "registered" : ["refunded", "cancelled"].includes(r.payment_status) ? "cancelled" : r.payment_status}</span></td>
              <td className="px-4 py-2 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No registrations yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function SubmissionsView({ items, onApprove, onDecline }) {
  const [declineTarget, setDeclineTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  const method = (s) => (s.extra?.id_verification || "");
  const uploaded = (s) => method(s).toLowerCase().includes("upload");

  const doApprove = async (s) => { setBusy(s.id); await onApprove(s.id); setBusy(null); };
  const doDecline = async () => {
    if (!declineTarget) return;
    setBusy(declineTarget.id);
    const ok = await onDecline(declineTarget.id, reason);
    setBusy(null);
    if (ok) { setDeclineTarget(null); setReason(""); }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="admin-submissions">
      {items.map((s) => (
        <div key={s.id} className="bg-navy-paper border border-slate-800 p-4" data-testid={`submission-${s.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-white font-medium text-sm">{s.name} <span className="text-slate-500 text-xs">· {s.email} {s.phone && `· ${s.phone}`}</span></div>
            <div className="flex items-center gap-2">
              {s.status === "approved" && <span className="text-accent text-[10px] uppercase tracking-widest border border-accent/40 px-2 py-0.5" data-testid={`submission-status-${s.id}`}>Legend · #{s.member_id}</span>}
              {s.status === "declined" && <span className="text-destructive text-[10px] uppercase tracking-widest border border-destructive/40 px-2 py-0.5" data-testid={`submission-status-${s.id}`}>Declined</span>}
              <span className="text-gold text-[10px] uppercase tracking-widest border border-gold/30 px-2 py-0.5">{(s.type || "").replace("_", " ")}</span>
            </div>
          </div>
          {s.message && <p className="text-slate-400 text-sm mt-2">{s.message}</p>}
          {s.extra && Object.keys(s.extra).length > 0 && (
            <div className="text-slate-500 text-xs mt-2 flex flex-wrap gap-x-3 gap-y-1 items-center">
              {Object.entries(s.extra).map(([k, v]) => {
                if (!v) return null;
                if (k === "id_document") return <a key={k} href={v.startsWith("http") ? v : `${process.env.REACT_APP_BACKEND_URL}${v}`} target="_blank" rel="noreferrer" className="text-gold underline" data-testid="view-id-link">View uploaded ID</a>;
                return <span key={k}>{k.replace(/_/g, " ")}: {v}</span>;
              })}
            </div>
          )}
          {s.type === "become_legend" && s.status !== "approved" && s.status !== "declined" && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
              <Button onClick={() => doApprove(s)} disabled={busy === s.id} className="rounded-none bg-gold text-navy hover:bg-gold-light text-xs uppercase tracking-widest font-bold" data-testid={`approve-${s.id}`}>
                {uploaded(s) ? "Add Legend" : "Add Member"}
              </Button>
              <Button onClick={() => { setDeclineTarget(s); setReason(""); }} disabled={busy === s.id} variant="outline" className="rounded-none border-destructive/50 text-destructive hover:bg-destructive hover:text-white text-xs uppercase tracking-widest" data-testid={`decline-${s.id}`}>
                Decline
              </Button>
            </div>
          )}
        </div>
      ))}
      {items.length === 0 && <p className="text-slate-500 text-center py-12">No enquiries yet.</p>}

      <Dialog open={!!declineTarget} onOpenChange={(o) => { if (!o) { setDeclineTarget(null); setReason(""); } }}>
        <DialogContent className="bg-navy-paper border-slate-800 rounded-none" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-display">Decline Application</DialogTitle>
            <DialogDescription className="text-slate-400">Let {declineTarget?.name || "the applicant"} know why. This reason is emailed to them.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="Reason for declining…" className="rounded-none bg-navy border-slate-700 text-sm" data-testid="decline-reason" />
          <DialogFooter>
            <Button onClick={doDecline} disabled={busy === declineTarget?.id} className="rounded-none bg-destructive text-white hover:bg-destructive/90 text-xs uppercase tracking-widest" data-testid="decline-confirm">Decline & Email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMAIL_AREAS = [
  { key: "welcome", label: "Welcome / Add Legend", placeholders: "{name}, {member_id}, {link}" },
  { key: "decline", label: "Decline Application", placeholders: "{name}, {reason}" },
];

export function EmailTemplatesAdmin() {
  const [data, setData] = useState({ templates: [], defaults: {} });
  const [editing, setEditing] = useState(null);
  const empty = { id: "", area: "welcome", name: "", subject: "", body: "" };
  const [f, setF] = useState(empty);

  const load = () => api.get("/admin/email-templates").then((r) => setData(r.data || { templates: [], defaults: {} })).catch(() => {});
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    try { const { data: saved } = await api.put("/admin/email-templates", { data: next }); setData(saved); toast.success("Email templates saved"); return true; }
    catch (e) { toast.error("Could not save templates"); return false; }
  };

  const startEdit = (t) => { setEditing(t.id); setF({ ...empty, ...t }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const cancel = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    const id = editing || `tpl_${Date.now()}`;
    const tpl = { ...f, id };
    const templates = editing ? data.templates.map((t) => (t.id === id ? tpl : t)) : [...data.templates, tpl];
    const defaults = { ...data.defaults };
    if (!defaults[tpl.area]) defaults[tpl.area] = id;
    const ok = await persist({ ...data, templates, defaults });
    if (ok) cancel();
  };

  const del = async (id) => {
    const templates = data.templates.filter((t) => t.id !== id);
    const defaults = { ...data.defaults };
    Object.keys(defaults).forEach((a) => { if (defaults[a] === id) delete defaults[a]; });
    await persist({ ...data, templates, defaults });
  };

  const setDefault = async (area, id) => persist({ ...data, defaults: { ...data.defaults, [area]: id } });

  const activeArea = EMAIL_AREAS.find((a) => a.key === f.area);

  return (
    <Panel title={editing ? "Edit Template" : "New Email Template"} form={
      <form onSubmit={submit} className="space-y-3" data-testid="email-template-form">
        <div>
          <Label className="text-slate-300 text-xs">Email Type</Label>
          <select value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-none bg-navy border border-slate-700 text-white text-sm" data-testid="email-template-area">
            {EMAIL_AREAS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
        </div>
        <Field label="Template Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Friendly Welcome" />
        <Field label="Subject" required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
        <div>
          <Label className="text-slate-300 text-xs">Body</Label>
          <Textarea required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={8} className="mt-1 rounded-none bg-navy border-slate-700 text-sm" data-testid="email-template-body" />
          <p className="text-slate-500 text-[11px] mt-1">Placeholders: <span className="text-gold">{activeArea?.placeholders}</span></p>
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="email-template-submit">{editing ? "Save Changes" : "Add Template"}</Button>
          {editing && <Button type="button" onClick={cancel} variant="outline" className="rounded-none border-slate-600 text-slate-300 text-xs">Cancel</Button>}
        </div>
      </form>
    }>
      <div className="space-y-5">
        {EMAIL_AREAS.map((area) => {
          const list = data.templates.filter((t) => t.area === area.key);
          return (
            <div key={area.key} data-testid={`email-area-${area.key}`}>
              <div className="text-gold text-xs uppercase tracking-widest mb-2">{area.label}</div>
              {list.length === 0 && <p className="text-slate-500 text-xs mb-2">No templates yet — using the built-in default.</p>}
              <div className="space-y-2">
                {list.map((t) => (
                  <div key={t.id} className={`bg-navy-paper border p-3 ${data.defaults?.[area.key] === t.id ? "border-gold/50" : "border-slate-800"}`} data-testid={`email-template-${t.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-white text-sm font-medium truncate">{t.name} {data.defaults?.[area.key] === t.id && <span className="text-gold text-[10px] uppercase ml-1">· Default</span>}</div>
                        <div className="text-slate-500 text-xs truncate">{t.subject}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {data.defaults?.[area.key] !== t.id && <button onClick={() => setDefault(area.key, t.id)} className="text-slate-400 hover:text-gold text-[10px] uppercase tracking-widest border border-slate-700 px-2 py-1" data-testid={`set-default-${t.id}`}>Set Default</button>}
                        <button onClick={() => startEdit(t)} className="text-slate-500 hover:text-gold"><Pencil className="w-4 h-4" /></button>
                        <DelBtn onClick={() => del(t.id)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function EditPlayerDialog({ player, onClose, onSave }) {
  const [f, setF] = useState({ email: "", phone: "", birthdate: "" });
  useEffect(() => { if (player) setF({ email: player.email || "", phone: player.phone || "", birthdate: player.birthdate || "" }); }, [player]);
  return (
    <Dialog open={!!player} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-navy-paper border-slate-800 rounded-none" data-testid="edit-player-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display">Edit {player?.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Changing the email also changes the address this member uses to log in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="edit-player-email" />
          <Field label="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="e.g. 0400 123 456" data-testid="edit-player-phone" />
          <Field label="Date of Birth" type="date" value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} data-testid="edit-player-birthdate" />
        </div>
        <DialogFooter>
          <Button onClick={async () => { if (await onSave(player.user_id, f)) onClose(); }} disabled={!f.email.trim()} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="edit-player-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PlayersView({ items, onUpdate }) {
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(null);
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const digits = term.replace(/\D/g, "");
  const shown = !term ? items : items.filter((p) =>
    [p.name, p.email, p.member_id].some((v) => (v || "").toLowerCase().includes(term)) ||
    (digits && (p.phone || "").replace(/\D/g, "").includes(digits)));
  const resend = async (p) => {
    if (!window.confirm(`Send a new set-password email to ${p.name} (${p.email})? Any earlier link will stop working.`)) return;
    setSending(p.user_id);
    try {
      await api.post("/admin/members/invite", { name: p.name, email: p.email });
      toast.success(`Set-password email sent to ${p.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the email");
    } finally { setSending(null); }
  };
  return (
    <div className="mt-6">
    <div className="relative max-w-md mb-4">
      <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, member #, email or phone" className="w-full bg-navy-paper border border-slate-700 focus:border-gold outline-none text-white text-sm pl-9 pr-3 py-2.5" data-testid="players-search" />
      {term && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" data-testid="players-search-count">{shown.length} found</span>}
    </div>
    <div className="border border-white/10 overflow-x-auto" data-testid="admin-players">
      <table className="w-full text-left text-sm min-w-[1140px]">
        <thead className="bg-secondary text-gold text-xs uppercase"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Member #</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Date of Birth</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Via</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3"></th></tr></thead>
        <tbody>
          {shown.map((p) => (
            <tr key={p.user_id} className="border-t border-white/5" data-testid={`player-row-${p.user_id}`}>
              <td className="px-4 py-2 text-white">{p.name}</td>
              <td className="px-4 py-2 text-slate-300 font-mono">{p.member_id || "—"}</td>
              <td className="px-4 py-2 text-slate-400">{p.email}</td>
              <td className="px-4 py-2 text-slate-400" data-testid={`player-phone-${p.user_id}`}>{p.phone || "—"}</td>
              <td className="px-4 py-2 text-slate-400 whitespace-nowrap" data-testid={`player-dob-${p.user_id}`}>{p.birthdate ? p.birthdate.split("-").reverse().join("/") : "—"}</td>
              <td className="px-4 py-2"><span className={p.role === "admin" ? "text-gold" : "text-slate-300"}>{p.role}</span></td>
              <td className="px-4 py-2 text-slate-500">{p.auth_provider}</td>
              <td className="px-4 py-2 text-slate-500">{(p.created_at || "").slice(0, 10)}</td>
              <td className="px-4 py-2">
                <div className="flex items-center gap-3">
                  <button onClick={() => setEditing(p)} className="text-gold hover:text-gold-light" title="Edit email / phone" data-testid={`player-edit-${p.user_id}`}><Pencil className="w-4 h-4" /></button>
                  {p.role !== "admin" && (
                    <button onClick={() => resend(p)} disabled={sending === p.user_id} className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-gold disabled:opacity-50 whitespace-nowrap" title="Resend set-password email" data-testid={`player-resend-invite-${p.user_id}`}>
                      {sending === p.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Resend set-password email
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {shown.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500" data-testid="players-search-empty">No members match “{q}”.</td></tr>}
        </tbody>
      </table>
      <EditPlayerDialog player={editing} onClose={() => setEditing(null)} onSave={onUpdate} />
    </div>
    </div>
  );
}
